import { useEffect, useState, useCallback, useSyncExternalStore } from "react";

const KEY = "cart_v1";
const listeners = new Set();

let snapshot = null; // cached snapshot reference for getSnapshot stability

function load() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch {
    return [];
  }
}

function getSnapshot() {
  if (snapshot === null) snapshot = load();
  return snapshot;
}

function refresh() {
  snapshot = load();
  listeners.forEach((l) => l());
}

function write(items) {
  localStorage.setItem(KEY, JSON.stringify(items));
  snapshot = items;
  listeners.forEach((l) => l());
}

function subscribe(cb) {
  listeners.add(cb);
  // Sync on storage events from other tabs
  const storageHandler = (e) => {
    if (e.key === KEY) {
      refresh();
    }
  };
  window.addEventListener("storage", storageHandler);
  return () => {
    listeners.delete(cb);
    window.removeEventListener("storage", storageHandler);
  };
}

const EMPTY = [];
function getServerSnapshot() {
  return EMPTY;
}

export function useCart() {
  const items = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const addItem = useCallback((product, qty = 1) => {
    const current = [...getSnapshot()];
    const idx = current.findIndex((i) => i.id === product.id);
    if (idx >= 0) {
      current[idx] = { ...current[idx], quantity: current[idx].quantity + qty };
    } else {
      current.push({
        id: product.id,
        name: product.name,
        category: product.category,
        price: product.price,
        photo: product.photo,
        strength: product.strength,
        quantity: qty,
      });
    }
    write(current);
  }, []);

  const removeItem = useCallback((id) => {
    write(getSnapshot().filter((i) => i.id !== id));
  }, []);

  const setQuantity = useCallback((id, qty) => {
    if (qty <= 0) return write(getSnapshot().filter((i) => i.id !== id));
    write(getSnapshot().map((i) => (i.id === id ? { ...i, quantity: qty } : i)));
  }, []);

  const clear = useCallback(() => write([]), []);

  const totalQty = items.reduce((s, i) => s + i.quantity, 0);
  const totalPrice = items.reduce((s, i) => s + i.price * i.quantity, 0);

  return { items, addItem, removeItem, setQuantity, clear, totalQty, totalPrice };
}

export function useCartCount() {
  const [count, setCount] = useState(() =>
    getSnapshot().reduce((s, i) => s + i.quantity, 0),
  );
  useEffect(() => {
    const update = () =>
      setCount(getSnapshot().reduce((s, i) => s + i.quantity, 0));
    listeners.add(update);
    return () => listeners.delete(update);
  }, []);
  return count;
}
